import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Pagination } from '@/components/UI/Pagination';
import type { LogLevel } from '@/redux/internal-services/platformLogsApi';

export const PAGE_SIZE = 50;

/** Filters owned by the page and shared by every tab. */
export interface SharedLogFilters {
  tenantId: string;
  fromDate: string;
  toDate: string;
  /** Open the Trace tab for this correlation id. */
  onTrace: (correlationId: string) => void;
}

/** `datetime-local` value (local time) → ISO string the API accepts. */
export function toIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** API datetimes are UTC; naive ones (no offset) are read as UTC, not local time. */
export function fmtTime(value: string | null | undefined): string {
  if (!value) return '—';
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

const LEVEL_VARIANT: Record<string, BadgeProps['variant']> = {
  DEBUG: 'neutral',
  INFO: 'steel',
  WARNING: 'warning',
  ERROR: 'danger',
  CRITICAL: 'danger',
};

export function LevelBadge({ level }: { level: LogLevel | string }) {
  return <Badge variant={LEVEL_VARIANT[level] ?? 'neutral'}>{level}</Badge>;
}

/** A correlation id that jumps to the Trace tab. */
export function TraceLink({ id, onTrace }: { id: string | null | undefined; onTrace: (id: string) => void }) {
  if (!id) return <span className="text-ui-text3">—</span>;
  return (
    <Button
      variant="link"
      size="xs"
      className="font-mono text-accent-text"
      title="Open the full trace for this request"
      onClick={() => onTrace(id)}
    >
      {id.length > 14 ? `${id.slice(0, 12)}…` : id}
    </Button>
  );
}

export function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-chip bg-ui-subtle px-3 py-2 font-mono text-xs text-ui-text2">
      {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

export function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="eyebrow">{label}</span>
      <span className="break-all text-sm text-ui-text">{children ?? '—'}</span>
    </div>
  );
}

export function PagedFooter({
  skip,
  total,
  onSkip,
}: {
  skip: number;
  total: number;
  onSkip: (skip: number) => void;
}) {
  if (total <= PAGE_SIZE) return null;
  return (
    <div className="flex flex-col items-center gap-1 text-xs text-ui-text2">
      <span>
        Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}
      </span>
      <Pagination
        page={Math.floor(skip / PAGE_SIZE)}
        totalPages={Math.ceil(total / PAGE_SIZE)}
        onPageChange={(p) => onSkip(p * PAGE_SIZE)}
        previousLabel="Prev"
        className="pt-0"
      />
    </div>
  );
}
